import React from 'react';
import PropTypes from 'prop-types';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/* ================= Conversión HTML a texto para PDF ================= */
function htmlToPdfLines(textOrHtml = '') {
  const root = document.createElement('div');
  const s = String(textOrHtml ?? '');
  const sanitized = s
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '');
  root.innerHTML = sanitized;

  const lines = [];
  const push = (t = '') => { lines.push(t.replace(/\s+$/,'')); };
  const INDENT = (n) => '  '.repeat(n);
  
  const walk = (node, indent = 0) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const txt = node.nodeValue.replace(/\s+/g, ' ');
      if (txt.trim()) {
        if (!lines.length) push('');
        lines[lines.length - 1] += txt;
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName.toLowerCase();

    if (tag === 'br') { push(''); return; }

    if (tag === 'p' || tag === 'div') {
      const before = lines.length;
      node.childNodes.forEach(ch => walk(ch, indent));
      if (lines.length === before) push('');
      push('');
      return;
    }

    if (tag === 'ul' || tag === 'ol') {
      let idx = 1;
      Array.from(node.children).forEach(li => {
        if (li.tagName && li.tagName.toLowerCase() === 'li') {
          const bullet = tag === 'ul' ? '• ' : `${idx}. `;
          push(INDENT(indent) + bullet);
          const start = lines.length - 1;
          li.childNodes.forEach(ch => walk(ch, indent + 1));
          if (lines[start] && lines[start].trim() === bullet.trim()) lines[start] += ' ';
          idx += 1;
        }
      });
      push('');
      return;
    }

    if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6') {
      push('');
      push('## ' + (node.textContent || '').trim());
      push('');
      return;
    }

    if (tag === 'strong' || tag === 'b') {
      lines[lines.length - 1] += '**';
      node.childNodes.forEach(ch => walk(ch, indent));
      lines[lines.length - 1] += '**';
      return;
    }

    if (tag === 'em' || tag === 'i') {
      lines[lines.length - 1] += '*';
      node.childNodes.forEach(ch => walk(ch, indent));
      lines[lines.length - 1] += '*';
      return;
    }

    if (tag === 'pre' || tag === 'code') {
      const text = (node.textContent || '').replace(/\r\n/g, '\n');
      push('');
      text.split('\n').forEach(ln => push(INDENT(indent) + '  ' + ln));
      push('');
      return;
    }

    if (tag === 'blockquote') {
      node.childNodes.forEach(ch => {
        const before = lines.length;
        walk(ch, indent);
        for (let i = before; i < lines.length; i++) {
          lines[i] = '│ ' + lines[i];
        }
      });
      push('');
      return;
    }

    node.childNodes.forEach(ch => walk(ch, indent));
  };

  walk(root, 0);
  while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
  return lines.length ? lines : ['—'];
}

/* ================= Helpers de fecha/hora ================= */
const pad2 = (n) => String(n).padStart(2,'0');

function hhmm(str) {
  if (!str) return null;
  const m = String(str).match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]); const mi = Number(m[2]);
  if (isNaN(h) || isNaN(mi)) return null;
  return `${pad2(h)}:${pad2(mi)}`;
}

function dateYYYYMMDD(str) {
  if (!str) return null;
  const m = String(str).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

function pickDateFromISO(iso) {
  return dateYYYYMMDD(iso);
}

function pickTimeFromISO(iso) {
  if (!iso) return null;
  const m = String(iso).match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : null;
}

function buildDateTimeRange(l) {
  const d0 = dateYYYYMMDD(l?.date);
  const s0 = hhmm(l?.start || l?.start_time);
  const e0 = hhmm(l?.end || l?.end_time);
  if (d0 && (s0 || e0)) {
    if (s0 && e0) return `${d0} ${s0}–${e0}`;
    if (s0)       return `${d0} ${s0}`;
    return `${d0} ${e0}`;
  }
  const ds = pickDateFromISO(l?.started_at || l?.startedAt);
  const de = pickDateFromISO(l?.ended_at || l?.endedAt);
  const s1 = pickTimeFromISO(l?.started_at || l?.startedAt);
  const e1 = pickTimeFromISO(l?.ended_at || l?.endedAt);
  const d1 = ds || de || d0;
  if (d1 && (s1 || e1)) {
    if (s1 && e1) return `${d1} ${s1}–${e1}`;
    if (s1)       return `${d1} ${s1}`;
    return `${d1} ${e1}`;
  }
  if (l?.date_display) return l.date_display;
  if (l?.date_obj instanceof Date && !isNaN(l.date_obj)) {
    return `${l.date_obj.getFullYear()}-${pad2(l.date_obj.getMonth()+1)}-${pad2(l.date_obj.getDate())}`;
  }
  if (typeof l?.date === 'string') {
    const d = dateYYYYMMDD(l.date);
    if (d) return d;
  }
  return '—';
}

function defaultFormatDate(d) {
  try {
    const x = d instanceof Date ? d : (d ? new Date(d) : null);
    return x && !isNaN(x) ? x.toLocaleString('es-CL', { timeZone: 'America/Santiago' }) : '';
  } catch { return ''; }
}

function defaultFmtCLP(n) {
  return new Intl.NumberFormat('es-CL', { style:'currency', currency:'CLP', maximumFractionDigits:0 }).format(Number(n||0));
}

function defaultGetUserName(l) { 
  return l?.user_name || l?.member_name || l?.username || l?.user?.name || l?.user || '—'; 
}

function defaultGetHours(l) { 
  return Number(l?.hours ?? l?.hours_total ?? l?.hours_worked ?? 0); 
}

function defaultGetAmount(l) {
  const rate = Number(l?.hourly_rate_snapshot ?? l?.hourly_rate ?? 0);
  const hrs  = Number(l?.hours ?? 0);
  return Number(l?.amount_total ?? l?.amount ?? (rate * hrs) ?? 0);
}

/* ================= Componente ExportSingleLogPDF ================= */
export default function ExportSingleLogPDF({
  log,
  project,
  formatDate = defaultFormatDate,
  getUserName = defaultGetUserName,
  getHours    = defaultGetHours,
  getAmount   = defaultGetAmount,
  fmtCLP      = defaultFmtCLP,
  render,
  buttonProps = {},
}) {
  const onExport = React.useCallback(() => {
    if (!log) return;

    const doc = new jsPDF({ unit:'pt', format:'a4' });
    const margin = 50;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (margin * 2);
    const now = new Date();

    // Colores corporativos mejorados
    const primaryColor = [37, 99, 235]; // Azul
    const secondaryColor = [100, 116, 139]; // Gris
    const accentColor = [59, 130, 246]; // Azul claro
    const lightBg = [248, 250, 252]; // Gris muy claro

    let yPos = margin;

    // ========== HEADER PRINCIPAL ==========
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 80, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Avance de Bitácora', margin, 35);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Generado: ${formatDate(now)}`, margin, 55);

    yPos = 100;

    // ========== INFORMACIÓN DEL PROYECTO ==========
    if (project) {
      doc.setFillColor(...lightBg);
      doc.rect(margin, yPos, contentWidth, 60, 'F');
      
      doc.setTextColor(...secondaryColor);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('PROYECTO', margin + 10, yPos + 20);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.text(project?.name || '—', margin + 10, yPos + 37);
      
      doc.setTextColor(...secondaryColor);
      doc.setFontSize(9);
      doc.text(`Cliente: ${project?.client_name || '—'}`, margin + 10, yPos + 52);
      
      yPos += 75;
    }

    // ========== DETALLES DEL REGISTRO ==========
    const dateDisplay = buildDateTimeRange(log);
    const userName = getUserName(log) || '—';
    const hours = (getHours(log) ?? 0).toFixed(2);
    const amount = fmtCLP(getAmount(log) ?? 0);
    const activity = log?.activity_code || '—';
    const billable = log?.billable === 1 || log?.billable === true ? 'Sí' : 'No';

    // Tarjeta de detalles
    doc.setFillColor(...lightBg);
    doc.rect(margin, yPos, contentWidth, 110, 'F');

    const detailsData = [
      ['Fecha y Horario:', dateDisplay],
      ['Programador:', userName],
      ['Actividad:', activity],
      ['Horas:', `${hours} h`],
      ['Monto:', amount],
      ['Pagado:', billable],
    ];

    let detailY = yPos + 15;
    detailsData.forEach(([label, value]) => {
      doc.setTextColor(...secondaryColor);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text(label, margin + 10, detailY);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(value, margin + 120, detailY);
      
      detailY += 18;
    });

    yPos += 125;

    // ========== SEPARADOR ==========
    doc.setDrawColor(...accentColor);
    doc.setLineWidth(2);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 20;

    // ========== CONTENIDO DEL AVANCE ==========
    doc.setTextColor(...primaryColor);
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.text('Descripción del Avance', margin, yPos);
    yPos += 15;

    // Convertir HTML a líneas de texto
    const avanceHtml = log?.progress_note || log?.note || '';
    const avanceLines = htmlToPdfLines(avanceHtml);

    if (avanceLines.length === 1 && avanceLines[0] === '—') {
      doc.setTextColor(...secondaryColor);
      doc.setFontSize(10);
      doc.setFont(undefined, 'italic');
      doc.text('Sin descripción de avance.', margin, yPos);
      yPos += 20;
    } else {
      // Renderizar el contenido con mejor formato
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');

      const lineHeight = 14;
      const maxWidth = contentWidth - 20;
      
      avanceLines.forEach(line => {
        // Detectar encabezados
        if (line.startsWith('## ')) {
          yPos += 10;
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(...accentColor);
          const headerText = line.substring(3);
          const splitHeader = doc.splitTextToSize(headerText, maxWidth);
          splitHeader.forEach(subLine => {
            if (yPos > doc.internal.pageSize.getHeight() - margin - 30) {
              doc.addPage();
              yPos = margin;
            }
            doc.text(subLine, margin + 10, yPos);
            yPos += lineHeight + 2;
          });
          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(0, 0, 0);
          yPos += 5;
          return;
        }

        // Detectar bloques de código
        if (line.trim().startsWith('  ') && !line.trim().startsWith('• ') && !line.match(/^\s*\d+\./)) {
          doc.setFillColor(245, 245, 245);
          const codeText = line.trim();
          const splitCode = doc.splitTextToSize(codeText, maxWidth - 20);
          const codeBlockHeight = splitCode.length * lineHeight + 10;
          
          if (yPos + codeBlockHeight > doc.internal.pageSize.getHeight() - margin) {
            doc.addPage();
            yPos = margin;
          }
          
          doc.rect(margin + 10, yPos - 10, maxWidth, codeBlockHeight, 'F');
          doc.setFont('courier', 'normal');
          doc.setFontSize(9);
          doc.setTextColor(60, 60, 60);
          
          splitCode.forEach(subLine => {
            doc.text(subLine, margin + 15, yPos);
            yPos += lineHeight;
          });
          
          doc.setFont(undefined, 'normal');
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          yPos += 10;
          return;
        }

        // Detectar citas (blockquote)
        if (line.startsWith('│ ')) {
          doc.setDrawColor(...accentColor);
          doc.setLineWidth(3);
          const quoteText = line.substring(2);
          const splitQuote = doc.splitTextToSize(quoteText, maxWidth - 40);
          
          splitQuote.forEach(subLine => {
            if (yPos > doc.internal.pageSize.getHeight() - margin - 30) {
              doc.addPage();
              yPos = margin;
            }
            doc.line(margin + 10, yPos - 8, margin + 10, yPos + 3);
            doc.setTextColor(...secondaryColor);
            doc.setFont(undefined, 'italic');
            doc.text(subLine, margin + 20, yPos);
            yPos += lineHeight;
          });
          
          doc.setFont(undefined, 'normal');
          doc.setTextColor(0, 0, 0);
          yPos += 5;
          return;
        }

        // Texto normal (con soporte para listas)
        if (line.trim()) {
          const splitText = doc.splitTextToSize(line, maxWidth);
          splitText.forEach(subLine => {
            if (yPos > doc.internal.pageSize.getHeight() - margin - 30) {
              doc.addPage();
              yPos = margin;
            }
            doc.text(subLine, margin + 10, yPos);
            yPos += lineHeight;
          });
        } else {
          yPos += lineHeight / 2; // Espaciado para líneas vacías
        }
      });
    }

    // ========== FOOTER ==========
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const footerY = doc.internal.pageSize.getHeight() - 25;
      
      doc.setDrawColor(...lightBg);
      doc.setLineWidth(0.5);
      doc.line(margin, footerY - 10, pageWidth - margin, footerY - 10);
      
      doc.setTextColor(...secondaryColor);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text(`Página ${i} de ${totalPages}`, pageWidth - margin - 50, footerY);
      doc.text(`ID: ${log?.id || '—'}`, margin, footerY);
    }

    // ========== GUARDAR PDF ==========
    const safeName = (project?.name || 'Avance').replace(/[^\w\-]+/g,'_').slice(0,40);
    const dateStr = dateYYYYMMDD(log?.date) || now.toISOString().slice(0,10);
    const logId = log?.id || 'sin-id';
    doc.save(`Avance_${safeName}_${dateStr}_${logId}.pdf`);
  }, [log, project, formatDate, getUserName, getHours, getAmount, fmtCLP]);

  if (typeof render === 'function') {
    return render(onExport);
  }

  return (
    <button className="btn" onClick={onExport} {...buttonProps}>
      Descargar PDF
    </button>
  );
}

ExportSingleLogPDF.propTypes = {
  log: PropTypes.object.isRequired,
  project: PropTypes.object,
  formatDate: PropTypes.func,
  getUserName: PropTypes.func,
  getHours: PropTypes.func,
  getAmount: PropTypes.func,
  fmtCLP: PropTypes.func,
  render: PropTypes.func,
  buttonProps: PropTypes.object,
};
